import { useId, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { type FooterButton } from '@m/alchemy-ui/DialogFooter';
import { Select } from '@m/alchemy-ui/Select';
import { type PanelPlacement, type PanelPlacementPosition } from '../common/panel-placement';
import { PanelEditorLabel, PanelEditorSection, PanelInfoText } from '../CustomPage.styled';

const messages = defineMessages({
  movePanel: {
    id: 'editCustomPageMovePanel',
    defaultMessage: 'Move {panel}'
  },
  placePanel: {
    id: 'editCustomPagePlacePanel',
    defaultMessage: 'Place {panel}'
  },
  moveInstructions: {
    id: 'editCustomPageMovePanelInstructions',
    defaultMessage: 'Choose a new position for {panel}.'
  },
  placeInstructions: {
    id: 'editCustomPagePlacePanelInstructions',
    defaultMessage: 'Choose where to add {panel}.'
  },
  destination: {
    id: 'editCustomPagePanelDestination',
    defaultMessage: 'Position'
  },
  choosePosition: {
    id: 'editCustomPageChoosePanelPosition',
    defaultMessage: 'Choose a position'
  },
  rowPosition: {
    id: 'editCustomPagePanelRowPosition',
    defaultMessage: 'Row {row}, position {position} of {total}'
  },
  currentPosition: {
    id: 'editCustomPagePanelCurrentPosition',
    defaultMessage: '{position} (current position)'
  },
  newRowFirst: {
    id: 'editCustomPagePanelNewRowFirst',
    defaultMessage: 'New row before row 1'
  },
  newRowBetween: {
    id: 'editCustomPagePanelNewRowBetween',
    defaultMessage: 'New row between rows {before} and {after}'
  },
  newRowAfter: {
    id: 'editCustomPagePanelNewRowAfter',
    defaultMessage: 'New row after row {row}'
  },
  newRowEmpty: {
    id: 'editCustomPagePanelNewRowEmpty',
    defaultMessage: 'New row 1'
  },
  noPositions: {
    id: 'editCustomPageNoPanelPositions',
    defaultMessage: 'There are no other available positions.'
  },
  cancel: {
    id: 'cancel',
    defaultMessage: 'Cancel'
  },
  move: {
    id: 'move',
    defaultMessage: 'Move'
  },
  add: {
    id: 'add',
    defaultMessage: 'Add'
  }
});

const getPositionKey = (position: PanelPlacementPosition): string =>
  position.type === 'row' ? `row:${position.rowIndex}:${position.panelIndex}` : `row-drop-zone:${position.rowIndex}`;

interface PanelPlacementDialogProps {
  readonly mode: 'move' | 'add';
  readonly panelName: string;
  readonly rowCount: number;
  readonly placements: readonly PanelPlacement[];
  readonly onCancel: () => void;
  readonly onConfirm: (placement: PanelPlacement) => void;
}

export const PanelPlacementDialog = ({
  mode,
  panelName,
  rowCount,
  placements,
  onCancel,
  onConfirm
}: PanelPlacementDialogProps) => {
  const intl = useIntl();
  const id = useId();
  const [selectedPositionKey, setSelectedPositionKey] = useState('');
  const labelId = `panel-placement-position-label-${id}`;

  const placementByKey = useMemo(
    () => new Map(placements.map((placement) => [getPositionKey(placement.position), placement])),
    [placements]
  );
  const options = useMemo(
    () =>
      placements.map(({ position, location, isCurrent }) => {
        const label =
          position.type === 'row'
            ? intl.formatMessage(messages.rowPosition, {
                row: position.rowIndex + 1,
                position: location.panelIndex + 1,
                total: location.rowLength
              })
            : rowCount === 0
              ? intl.formatMessage(messages.newRowEmpty)
              : position.rowIndex === 0
                ? intl.formatMessage(messages.newRowFirst)
                : position.rowIndex === rowCount
                  ? intl.formatMessage(messages.newRowAfter, { row: rowCount })
                  : intl.formatMessage(messages.newRowBetween, {
                      before: position.rowIndex,
                      after: position.rowIndex + 1
                    });

        return {
          value: getPositionKey(position),
          label: isCurrent ? intl.formatMessage(messages.currentPosition, { position: label }) : label,
          disabled: isCurrent,
          ...(isCurrent ? { 'data-testid': 'panel-placement-current-option' } : {})
        };
      }),
    [intl, placements, rowCount]
  );

  const selectedPlacement = placementByKey.get(selectedPositionKey);
  const hasDestination = placements.some(({ isCurrent }) => !isCurrent);
  const footerButtons = useMemo<(FooterButton & { 'data-testid': string })[]>(
    () => [
      {
        label: intl.formatMessage(messages.cancel),
        onClick: onCancel,
        priority: 'tertiary',
        'data-testid': 'panel-placement-cancel-button'
      },
      {
        label: intl.formatMessage(mode === 'move' ? messages.move : messages.add),
        onClick: () => selectedPlacement && !selectedPlacement.isCurrent && onConfirm(selectedPlacement),
        disabled: !selectedPlacement || selectedPlacement.isCurrent,
        'data-testid': 'panel-placement-confirm-button'
      }
    ],
    [intl, mode, onCancel, onConfirm, selectedPlacement]
  );

  return (
    <Dialog
      isOpen
      onClose={onCancel}
      headerContent={intl.formatMessage(mode === 'move' ? messages.movePanel : messages.placePanel, {
        panel: panelName
      })}
      bodyContent={
        <PanelEditorSection>
          <PanelInfoText>
            {intl.formatMessage(mode === 'move' ? messages.moveInstructions : messages.placeInstructions, {
              panel: panelName
            })}
          </PanelInfoText>
          {placements.length > 0 && (
            <>
              <PanelEditorLabel id={labelId}>{intl.formatMessage(messages.destination)}</PanelEditorLabel>
              <Select
                labelId={labelId}
                isFullWidth
                value={selectedPositionKey}
                placeholder={intl.formatMessage(messages.choosePosition)}
                options={options}
                onChange={(option) => setSelectedPositionKey(option.value)}
                data-testid="panel-placement-select"
              />
            </>
          )}
          {!hasDestination && <PanelInfoText>{intl.formatMessage(messages.noPositions)}</PanelInfoText>}
        </PanelEditorSection>
      }
      footerButtons={footerButtons}
      focusTrapOptions={{ returnFocusOnDeactivate: false }}
      data-testid="panel-placement-dialog"
    />
  );
};
